import { Component, forwardRef, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    ReactiveFormsModule,
    NG_VALUE_ACCESSOR,
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    FormControl,
    Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { lastValueFrom } from 'rxjs';
import { CoveragesService } from '../../services/coverages.service';
import { CurrenciesService } from '../../services/currencies.service';
import {
    CoverageListDto,
    CreateInsuranceTypeCoverageDto,
    InsuranceTypeCoverageDto,
    CurrencyEntryDto
} from '../../../../core/data/new-dtos/insurance-type.dtos';
import { CoverageItemComponent } from '../coverage-item/coverage-item.component';
import { SharedModule } from '../../../../common/shared.module';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { CoverageModalComponent, CoverageModalResult } from '../../../coverages/ui/coverage-modal/coverage-modal.component';

@Component({
    selector: 'app-coverage-selector',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, NgSelectModule, SharedModule, CoverageItemComponent, CoverageModalComponent],
    templateUrl: './coverage-selector.component.html',
    styleUrls: ['./coverage-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CoverageSelectorComponent),
            multi: true,
        },
        provideNgxMask(),
    ],
})
export class CoverageSelectorComponent implements ControlValueAccessor, OnInit, OnChanges {
    @Input() selectedCurrencyIds: number[] = [];
    @Input() defaultMaxAge: number = 100;
    @Input() defaultMaxDurationAge: number = 0;
    @Input() formulaId!: string;

    coverages: CoverageListDto[] = [];
    allCurrencies: CurrencyEntryDto[] = [];
    selectedCoverages: CreateInsuranceTypeCoverageDto[] = [];
    disabled = false;
    loading = false;
    showCoverageModal = false;

    // State properties (cached)
    availableCurrencies: CurrencyEntryDto[] = [];
    selectedCoverageIds: number[] = [];
    availableCoverages: CoverageListDto[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedCurrencyIds']) {
            this.updateAvailableCurrencies();
        }

        if (changes['defaultMaxAge'] || changes['defaultMaxDurationAge']) {
            this.selectedCoverages = this.selectedCoverages.map(c => {
                if (c.isRequired) {
                    return {
                        ...c,
                        maxAge: this.defaultMaxAge,
                        maxDurationAge: this.defaultMaxDurationAge
                    };
                }
                return c;
            });
            // Do not emit change here to avoid cycles. 
            // The parent controls these defaults, so it knows they changed.
            // The child component just updates its internal state for future user interactions.
        }
    }

    async updateAvailableCurrencies() {
        // If we have selected IDs that are not in our allCurrencies list, we need to refresh
        const missingIds = this.selectedCurrencyIds.filter(id => !this.allCurrencies.some(c => c.id === id));

        if (missingIds.length > 0) {
            await this.loadCurrencies();
        }

        this.availableCurrencies = this.allCurrencies.filter(c => this.selectedCurrencyIds.includes(c.id));

        // Sync currencies for all coverages (in-place, no emit to avoid loops)
        this.selectedCoverages.forEach(coverage => {
            this.availableCurrencies.forEach(global => {
                let currencyEntry = coverage.currencies.find(c => c.currencyId === global.id);

                // If currency missing in coverage, add it
                if (!currencyEntry) {
                    currencyEntry = {
                        currencyId: global.id,
                        minAmount: 0,
                        maxAmount: 0,
                        minPremium: null
                    };
                    coverage.currencies.push(currencyEntry);
                }

                // If coverage is required, always enforce global limits unless it is a fixed limit
                if (coverage.isRequired && !currencyEntry.isFixedLimit) {
                    currencyEntry.minAmount = global.min;
                    currencyEntry.maxAmount = global.max;
                }
            });
        });
    }

    updateAvailableCoverages() {
        this.selectedCoverageIds = this.selectedCoverages.map(sc => sc.coverageId);
        this.availableCoverages = this.coverages.filter(c => !this.selectedCoverageIds.includes(c.id));
    }

    coverageToAddControl = new FormControl<number | null>(null);

    private onChange: (value: CreateInsuranceTypeCoverageDto[]) => void = () => { };
    private onTouched: () => void = () => { };

    constructor(
        private coveragesService: CoveragesService,
        private currenciesService: CurrenciesService,
        private fb: FormBuilder,
    ) { }

    async ngOnInit() {
        this.coverageToAddControl.valueChanges.subscribe(val => {
            if (val) {
                this.addCoverage(val);
                this.coverageToAddControl.setValue(null, { emitEvent: false });
            }
        });

        await Promise.all([this.loadCoverages(), this.loadCurrencies()]);
    }

    async loadCurrencies() {
        try {
            this.allCurrencies = await lastValueFrom(this.currenciesService.getAll());
            this.updateAvailableCurrencies();
        } catch (e) {
            console.error('Error loading currencies', e);
        }
    }

    async loadCoverages() {
        this.loading = true;
        try {
            this.coverages = await lastValueFrom(this.coveragesService.getAll());
            this.updateAvailableCoverages();
        } catch (e) {
            console.error('Error loading coverages', e);
        } finally {
            this.loading = false;
        }
    }

    writeValue(value: CreateInsuranceTypeCoverageDto[] | null): void {
        this.selectedCoverages = value?.map(x => ({ ...x })) ?? [];
        this.updateAvailableCoverages();
    }

    registerOnChange(fn: (value: CreateInsuranceTypeCoverageDto[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }



    addCoverage(event: any): void {
        if (!event) return;

        let coverageId: number;
        if (typeof event === 'object' && event !== null && 'id' in event) {
            coverageId = event.id;
        } else {
            coverageId = event;
        }

        // Prevent duplicates
        if (this.selectedCoverages.some(sc => sc.coverageId === coverageId)) {
            return;
        }

        const newCoverage: CreateInsuranceTypeCoverageDto = {
            coverageId: coverageId,
            isRequired: false,
            insurePrimaryFraction: 0,
            maxAge: this.defaultMaxAge,
            maxDurationAge: this.defaultMaxDurationAge,
            isCalculated: false,
            // Initialize with all available currencies
            currencies: this.availableCurrencies.map(c => ({
                currencyId: c.id,
                minAmount: 0,
                maxAmount: 0,
                minPremium: null
            })),
            categories: []
        };

        this.selectedCoverages = [...this.selectedCoverages, newCoverage];
        this.updateAvailableCoverages();
        this.emitChange();
    }

    removeCoverage(index: number): void {
        this.selectedCoverages.splice(index, 1);
        this.selectedCoverages = [...this.selectedCoverages];
        this.updateAvailableCoverages();
        this.emitChange();
    }

    updateCoverage(index: number, field: string, value: any): void {
        const coverage = this.selectedCoverages[index];
        (coverage as any)[field] = value;

        this.emitChange();
    }

    onCoverageItemChange(index: number, updatedCoverage: CreateInsuranceTypeCoverageDto | InsuranceTypeCoverageDto): void {
        const coverage = updatedCoverage as CreateInsuranceTypeCoverageDto;
        if (coverage.isRequired) {
            // Enforce global limits for required coverage unless it is a fixed limit
            coverage.currencies.forEach(c => {
                const global = this.availableCurrencies.find(g => g.id === c.currencyId);
                if (global && !c.isFixedLimit) {
                    c.minAmount = global.min;
                    c.maxAmount = global.max;
                }
            });

            // Sync ages with defaults
            coverage.maxAge = this.defaultMaxAge;
            coverage.maxDurationAge = this.defaultMaxDurationAge;
        }

        this.selectedCoverages[index] = coverage;
        this.selectedCoverages = [...this.selectedCoverages];
        this.emitChange();
    }

    trackByIndex(index: number, obj: any): any {
        return index;
    }

    emitChange() {
        this.onChange(this.selectedCoverages);
        this.onTouched();
    }

    getCoverageName(id: number): string {
        // Use loose equality to handle string/number mismatch
        return this.coverages.find(c => c.id == id)?.name ?? 'Desconocida';
    }

    getCoverageCode(id: number): string {
        return this.coverages.find(c => c.id === id)?.coverageCode ?? '';
    }

    openCoverageModal(): void {
        this.showCoverageModal = true;
    }

    async onCoverageModalClosed(result: CoverageModalResult) {
        this.showCoverageModal = false;
        if (result.action === 'save' && result.data) {
            this.loading = true;
            try {
                const newId = await lastValueFrom(
                    this.coveragesService.create(result.data as any),
                );
                await this.loadCoverages();
                this.addCoverage(newId);
            } catch (e) {
                console.error('Error creating coverage', e);
            } finally {
                this.loading = false;
            }
        }
    }
}
